import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { Button } from '@/components/Button/Button';
import { LoadingScreen } from '@/components/LoadingScreen';

import { getGoogleSignInEmail } from '@/auth/accountEmail';
import { useAuth } from '@/auth/useAuth';
import { isValidEmail } from '@/features/profile/profileService';
import { useThemeRouteOverride } from '@/theme/useTheme';
import kuduLogoUrl from '@/images/kudu-logo-full.svg';

import '@/features/marketing/marketing-tokens.css';

import styles from './LoginPage.module.css';
import { getSafeReturnTo } from './returnTo';

type LoginStep =
  | 'initial'
  | 'checking'
  | 'password'
  | 'google'
  | 'both'
  | 'notRegistered';

type AuthAction = 'password' | 'google' | 'reset' | null;

const GOOGLE_PROVIDER_ID = 'google.com';
const PASSWORD_PROVIDER_ID = 'password';
const REDIRECT_EMAIL_STORAGE_KEY = 'kudu.login.expectedGoogleEmail';

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

function isPopupBlocked(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('code' in error)) {
    return false;
  }

  return error.code === 'auth/popup-blocked';
}

const CREDENTIAL_ERROR_MESSAGE = 'Email address or password is not correct.';

// Friendly, generic messages keyed by Firebase auth error code, so we never
// surface raw "Firebase: Error (auth/...)." strings to users. Credential
// failures intentionally share one message and do not reveal whether the email
// or the password was wrong.
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  'auth/invalid-credential': CREDENTIAL_ERROR_MESSAGE,
  'auth/invalid-email': CREDENTIAL_ERROR_MESSAGE,
  'auth/invalid-login-credentials': CREDENTIAL_ERROR_MESSAGE,
  'auth/missing-password': 'Enter your password.',
  'auth/network-request-failed':
    'Network error. Check your connection and try again.',
  'auth/too-many-requests':
    'Too many attempts. Please wait a moment and try again.',
  'auth/user-disabled': 'This account has been disabled.',
  'auth/user-not-found': CREDENTIAL_ERROR_MESSAGE,
  'auth/wrong-password': CREDENTIAL_ERROR_MESSAGE,
};

function getAuthErrorCode(error: unknown): string | null {
  if (error && typeof error === 'object' && 'code' in error) {
    const { code } = error as { code?: unknown };
    return typeof code === 'string' ? code : null;
  }

  return null;
}

function mapAuthError(error: unknown, fallback: string): string {
  const code = getAuthErrorCode(error);

  if (code && AUTH_ERROR_MESSAGES[code]) {
    return AUTH_ERROR_MESSAGES[code];
  }

  // Preserve genuine custom messages, but never leak raw Firebase error text.
  if (
    error instanceof Error &&
    error.message.trim().length > 0 &&
    !/firebase|auth\//i.test(error.message)
  ) {
    return error.message;
  }

  return fallback;
}

async function missingAuthFlowMethod(): Promise<never> {
  throw new Error('This authentication method is not available.');
}

// Without a way to tell, treat every Google sign-in as a new account: the
// email check then reads only the account's own email, as it always has.
async function assumeNewUser(): Promise<boolean> {
  return true;
}

function providersToStep(providers: string[]): LoginStep {
  const hasPassword = providers.includes(PASSWORD_PROVIDER_ID);
  const hasGoogle = providers.includes(GOOGLE_PROVIDER_ID);

  if (hasPassword && hasGoogle) {
    return 'both';
  }

  if (hasPassword) {
    return 'password';
  }

  if (hasGoogle) {
    return 'google';
  }

  return 'notRegistered';
}

export function LoginPage() {
  useThemeRouteOverride('light');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = getSafeReturnTo(searchParams.get('returnTo'));
  const {
    getGoogleRedirectResult = async () => null,
    isNewUser = assumeNewUser,
    loading,
    lookupEmailProviders = missingAuthFlowMethod,
    sendPasswordResetEmail = missingAuthFlowMethod,
    signInWithEmailAndPassword,
    signInWithGooglePopup = missingAuthFlowMethod,
    signInWithGoogleRedirect = missingAuthFlowMethod,
    signOut,
    user,
  } = useAuth();
  // A link may prefill the email, e.g. from a course staff invite.
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [confirmedEmail, setConfirmedEmail] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<LoginStep>('initial');
  const [authAction, setAuthAction] = useState<AuthAction>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const lookupRequestIdRef = useRef(0);

  useEffect(() => {
    const hasPendingGoogleRedirect =
      typeof window !== 'undefined' &&
      window.sessionStorage.getItem(REDIRECT_EMAIL_STORAGE_KEY);

    if (!loading && user && authAction === null && !hasPendingGoogleRedirect) {
      navigate(returnTo, { replace: true });
    }
  }, [authAction, loading, navigate, returnTo, user]);

  useEffect(() => {
    if (loading) {
      return;
    }

    let isCurrent = true;

    void (async () => {
      try {
        const credential = await getGoogleRedirectResult();

        if (!isCurrent || !credential) {
          return;
        }

        // Resolve the email before clearing the pending-redirect marker: while
        // it is set, the effect above will not navigate the now signed-in user
        // past this check. From here on a failed check always signs out, even
        // if this effect has been superseded.
        const actualEmail = getGoogleSignInEmail(credential.user, {
          // A failed lookup counts as new: the strict check.
          isNewUser: await isNewUser(credential).catch(() => true),
        });
        const expectedEmail =
          window.sessionStorage.getItem(REDIRECT_EMAIL_STORAGE_KEY) ?? '';
        window.sessionStorage.removeItem(REDIRECT_EMAIL_STORAGE_KEY);

        if (!actualEmail) {
          await signOut();
          setError('Google did not return an email address. Please try again.');
          setStep('initial');
          return;
        }

        if (!expectedEmail || actualEmail !== expectedEmail) {
          await signOut();
          setError(
            "The Google account you selected doesn't match the email you entered. Please try again.",
          );
          setStep('initial');
          return;
        }

        navigate(returnTo, { replace: true });
      } catch (redirectError) {
        if (!isCurrent) {
          return;
        }

        setError(
          mapAuthError(
            redirectError,
            'Unable to finish Google sign-in. Please try again.',
          ),
        );
      }
    })();

    return () => {
      isCurrent = false;
    };
  }, [getGoogleRedirectResult, isNewUser, loading, navigate, returnTo, signOut]);

  async function handleProviderLookup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (step !== 'initial' && step !== 'checking' && step !== 'notRegistered') {
      return;
    }

    const normalizedEmail = normalizeEmail(email);
    setError(null);
    setStatus(null);

    if (!isValidEmail(normalizedEmail)) {
      setError('Enter a valid email address.');
      return;
    }

    const requestId = lookupRequestIdRef.current + 1;
    lookupRequestIdRef.current = requestId;
    setEmail(normalizedEmail);
    setConfirmedEmail(normalizedEmail);
    setStep('checking');

    try {
      const providers = await lookupEmailProviders(normalizedEmail);

      if (lookupRequestIdRef.current !== requestId) {
        return;
      }

      setPassword('');
      setStep(providersToStep(providers));
    } catch (lookupError) {
      if (lookupRequestIdRef.current !== requestId) {
        return;
      }

      setStep('initial');
      setError(
        mapAuthError(
          lookupError,
          'Unable to check sign-in methods right now. Please try again.',
        ),
      );
    }
  }

  async function handlePasswordSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (step !== 'password' && step !== 'both') {
      return;
    }

    setError(null);
    setStatus(null);
    setAuthAction('password');

    try {
      await signInWithEmailAndPassword(confirmedEmail, password);
      navigate(returnTo, { replace: true });
    } catch (submitError) {
      setError(
        mapAuthError(
          submitError,
          'Unable to sign in with those credentials.',
        ),
      );
    } finally {
      setAuthAction(null);
    }
  }

  async function handlePasswordReset() {
    setError(null);
    setStatus(null);
    setAuthAction('reset');

    try {
      await sendPasswordResetEmail(confirmedEmail);
      setStatus(`Password reset link sent to ${confirmedEmail}. Check your inbox.`);
    } catch (resetError) {
      setError(
        mapAuthError(
          resetError,
          'Unable to send a password reset link right now.',
        ),
      );
    } finally {
      setAuthAction(null);
    }
  }

  async function startGoogleRedirect() {
    window.sessionStorage.setItem(REDIRECT_EMAIL_STORAGE_KEY, confirmedEmail);
    await signInWithGoogleRedirect();
  }

  async function handleGoogleSignIn() {
    setError(null);
    setStatus(null);
    setAuthAction('google');

    try {
      // Popup is the primary flow on every platform, including mobile: it
      // returns the credential via cross-window postMessage, which is immune to
      // the third-party-storage partitioning that breaks signInWithRedirect when
      // the auth domain (koob-polymer.firebaseapp.com) differs from the app's
      // origin. Redirect remains only as a popup-blocked fallback below.
      const credential = await signInWithGooglePopup();
      // A legacy account may have Google linked but no account email of its
      // own; then the email on its Google sign-in is what the sign-in proved.
      const actualEmail = getGoogleSignInEmail(credential.user, {
        // A failed lookup counts as new: the strict check.
        isNewUser: await isNewUser(credential).catch(() => true),
      });

      if (!actualEmail) {
        await signOut();
        setError('Google did not return an email address. Please try again.');
        return;
      }

      if (actualEmail !== confirmedEmail) {
        await signOut();
        setError(
          "The Google account you selected doesn't match the email you entered. Please try again.",
        );
        return;
      }

      navigate(returnTo, { replace: true });
    } catch (googleError) {
      if (isPopupBlocked(googleError)) {
        try {
          await startGoogleRedirect();
          return;
        } catch (redirectError) {
          setError(
            mapAuthError(
              redirectError,
              'Unable to start Google sign-in. Please try again.',
            ),
          );
          return;
        }
      }

      setError(
        mapAuthError(
          googleError,
          'Unable to sign in with Google. Please try again.',
        ),
      );
    } finally {
      setAuthAction(null);
    }
  }

  function handleUseDifferentEmail() {
    lookupRequestIdRef.current += 1;
    setConfirmedEmail('');
    setPassword('');
    setStep('initial');
    setAuthAction(null);
    setError(null);
    setStatus(null);
  }

  const canUsePassword = step === 'password' || step === 'both';
  const canUseGoogle = step === 'google' || step === 'both';
  const isChecking = step === 'checking';
  const isAuthenticating = authAction !== null;
  const hasLookedUpEmail = step !== 'initial' && step !== 'checking';

  if (loading) {
    return <LoadingScreen message="Preparing sign-in..." />;
  }

  return (
    <main className={styles.page}>
      <div className={styles.content}>
        <Link className={styles.wordmark} to="/">
          <img alt="Kudu" className={styles.wordmarkLogo} src={kuduLogoUrl} />
        </Link>

        <section aria-labelledby="login-title" className={styles.card}>
          <div className={styles.cardHeader}>
            <h1 className={styles.title} id="login-title">
              Sign in
            </h1>
            <p className={styles.description}>
              Enter your email first, and we&apos;ll show the sign-in methods
              already linked to your account.
            </p>
          </div>

          {step === 'initial' || step === 'checking' || step === 'notRegistered' ? (
            <form className={styles.form} noValidate onSubmit={handleProviderLookup}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="email">
                  Email
                </label>
                <input
                  aria-describedby={error ? 'login-error' : undefined}
                  aria-invalid={Boolean(error)}
                  autoComplete="email"
                  className={styles.input}
                  disabled={isChecking}
                  id="email"
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  type="email"
                  value={email}
                />
              </div>

              <Button
                className={styles.submitButton}
                disabled={isChecking}
                fullWidth
                size="large"
                type="submit"
                variant="primary"
              >
                {isChecking ? 'Checking...' : 'Continue'}
              </Button>
            </form>
          ) : null}

          {canUsePassword ? (
            <form className={styles.form} noValidate onSubmit={handlePasswordSubmit}>
              <p className={styles.emailSummary}>{confirmedEmail}</p>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="password">
                  Password
                </label>
                <input
                  autoComplete="current-password"
                  className={styles.input}
                  disabled={isAuthenticating}
                  id="password"
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  type="password"
                  value={password}
                />
              </div>

              <Button
                className={styles.submitButton}
                disabled={isAuthenticating}
                fullWidth
                size="large"
                type="submit"
                variant="primary"
              >
                {authAction === 'password' ? 'Signing in...' : 'Sign in'}
              </Button>

              <button
                className={styles.textButton}
                disabled={isAuthenticating}
                onClick={handlePasswordReset}
                type="button"
              >
                {authAction === 'reset' ? 'Sending reset link...' : 'Forgot password?'}
              </button>
            </form>
          ) : null}

          {step === 'notRegistered' ? (
            <div className={styles.optionGroup}>
              <p className={styles.notice}>
                No account found for {confirmedEmail}. Want to create one?
              </p>
              <Button
                as={Link}
                fullWidth
                size="large"
                to={`/signup?email=${encodeURIComponent(confirmedEmail)}&returnTo=${encodeURIComponent(returnTo)}`}
                variant="primary"
              >
                Create account
              </Button>
            </div>
          ) : null}

          {canUseGoogle ? (
            <div className={styles.optionGroup}>
              {step === 'both' ? (
                <p className={styles.notice}>
                  Multiple sign-in methods are linked to this account. For help
                  merging or clarifying duplicate accounts, email{' '}
                  <a href="mailto:support@kudu.com">support@kudu.com</a>.
                </p>
              ) : (
                <p className={styles.emailSummary}>{confirmedEmail}</p>
              )}
              <Button
                disabled={isAuthenticating}
                fullWidth
                onClick={handleGoogleSignIn}
                size="large"
                type="button"
                variant={step === 'both' ? 'secondary' : 'primary'}
              >
                {authAction === 'google' ? 'Opening Google...' : 'Continue with Google'}
              </Button>
            </div>
          ) : null}

          {hasLookedUpEmail ? (
            <button
              className={styles.textButton}
              disabled={isAuthenticating}
              onClick={handleUseDifferentEmail}
              type="button"
            >
              Use different email
            </button>
          ) : null}

          {status ? (
            <p aria-live="polite" className={styles.success}>
              {status}
            </p>
          ) : null}

          {error ? (
            <p className={styles.error} id="login-error" role="alert">
              {error}
            </p>
          ) : null}
        </section>
      </div>
    </main>
  );
}
